import { autoinject, computedFrom, transient } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RulesClientService } from "../../../../../../services/rules/rules-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { DataGrid } from "../../../../../components/data-grid/data-grid";
import { LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { QueryFilter } from "../../../../../../models/queries/queries.models";
import { BusinessUnitRuleCollectionModel } from "../../../../../../models/rules/rules.models";
import * as Enumerable from "linq";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject
@transient()
export class AssignRuleCollectionsDialog {

    dataGrid: DataGrid;
    selectedCustomers: {id: number, name: string, isSelected: boolean }[] = [];
    customers: {id: number, name: string, isSelected: boolean }[] = [];
    businessUnits: SimpleBusinessUnitModel[] = [];
    search: string;
    allSelected: boolean = false;
    ids: number[] = [];
    filters: QueryFilter[] = [];
    assignedBus: BusinessUnitRuleCollectionModel[] = [];

    constructor(private dialogClientService: DialogClientService, private rulesClientService: RulesClientService,
        private notificationService: NotificationClientService, private lookupService: LookupClientService) {
    }

    @computedFrom(Reflection.getPropertyPath<AssignRuleCollectionsDialog>(z => z.allSelected),
    Reflection.getPropertyPath<AssignRuleCollectionsDialog>(z => z.ids),
    Reflection.getPropertyPath<AssignRuleCollectionsDialog>(z => z.ids.length))
    get onlyOneSelected(): boolean {
        return this.allSelected != true && this.ids.length == 1;
    }

    async add(businessUnitId: number) {
        const existing = this.assignedBus.filter(x => x.businessUnitId == businessUnitId && x.ruleCollectionId == this.ids[0])[0];
        if (!existing) {
            this.assignedBus.push({
                businessUnitId: businessUnitId,
                ruleCollectionId: this.ids[0],
                isInternal: false,
                ruleCollectionPrompt: null
            });
        }
    }

    @computedFrom("businessUnits", "businessUnits.length", "search", "selectedCustomers", "selectedCustomers.length")
    get uiBusinessUnits() {
        const customerIds = this.selectedCustomers.map(x => x.id);
        return this.businessUnits.filter(x => !this.search || x.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1)
            .filter(x => !customerIds || customerIds.length == 0 || customerIds.indexOf(x.customerId) > -1);
    }

    @computedFrom("assignedBus", "assignedBus.length", "businessUnits", "search", "selectedCustomers", "selectedCustomers.length")
    get uiAssignedBus() {
        const customerIds = this.selectedCustomers.map(x => x.id);
        const buIds = this.assignedBus.map(x => x.businessUnitId);
        return this.businessUnits.filter(x => !this.search || x.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1)
            .filter(x => buIds.indexOf(x.id) > -1)
            .filter(x => !customerIds || customerIds.length == 0 || customerIds.indexOf(x.customerId) > -1);
    }

    async activate(model) {
        this.dataGrid = model.dataGrid;
        this.ids = model.ids;
        this.allSelected = model.allSelected;
        this.filters = model.filters;
        const request = new LookupValueRequest(LookupValueQuery.BusinessUnits, 'null,null,1');
        const businessUnits = await this.lookupService.getLookup(request);
        this.businessUnits = businessUnits.activeValues.map(x => { return { 
                id: x.id, 
                name: x.name + (x.additionalData && x.additionalData.customerName? ` (${x.additionalData.customerName})`: ''),
                isSelected: false,
                customerId: x.additionalData && x.additionalData.customerId? x.additionalData.customerId: null
            }; 
        });

        if (this.onlyOneSelected) {
            this.assignedBus = await this.rulesClientService.getRuleCollectionAssignments(this.ids[0]);

            this.customers = Enumerable.from(businessUnits.activeValues).select(x => { return x.additionalData && x.additionalData.customerId? { 
                    id: x.additionalData.customerId, 
                    name: x.additionalData.customerName,
                    isSelected: false 
                }: null; 
            }).where(x => x != null).distinct(x => x.id).toArray();
        }
    }

    unassignAll() {
        this.assignedBus = [];
    }

    assignAll() {
        for (const bu of this.uiBusinessUnits) {
            const existing = this.assignedBus.filter(x => x.businessUnitId == bu.id && x.ruleCollectionId == this.ids[0])[0];
            if (!existing) {
                this.assignedBus.push({
                    businessUnitId: bu.id,
                    ruleCollectionId: this.ids[0],
                    isInternal: false,
                    ruleCollectionPrompt: null
                });
            }
        }
    }

    isExisting(bu){
        return this.assignedBus.filter(x => x.businessUnitId == bu.id && x.ruleCollectionId == this.ids[0])[0];
    }

    unassign(businessUnit: { id: number }) {
        this.assignedBus = this.assignedBus.filter(x => x.businessUnitId != businessUnit.id);
    }

    close() {
        this.dialogClientService.hide();
    }

    async save() {
        try {
            const assignedBusinessUnitIds = this.businessUnits.filter(x => x.isSelected).map(x => x.id);
            await this.rulesClientService.assignRuleCollections({
                allSelected: this.allSelected,
                filters: this.filters,
                ids: this.ids,
                assignedBusinessUnitIds: assignedBusinessUnitIds,
                assignedBus: this.assignedBus
            });
            if (this.dataGrid) {
                this.dataGrid.unselectAll();
                this.dataGrid.toggleAll = false;
            }
            this.notificationService.show("Assigned successfully", "", NotificationType.Success);
            this.close();
        } catch (e) {
            this.notificationService.show("Assignment failed", "", NotificationType.Error);
        }
    }

}

export interface SimpleBusinessUnitModel {
    id: number;
    name: string;
    isSelected: boolean;
    customerId: number;
}
